import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { MessageSquarePlus, MessageCircle, RefreshCw, Edit2, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import MentionTextarea from '../../../components/MentionTextarea';
import MentionText from '../../../components/MentionText';

const ROLE_META = {
  admin: 'bg-slate-100 text-slate-700 border-slate-200',
  manager: 'bg-amber-50 text-amber-700 border-amber-200',
  trainer: 'bg-violet-50 text-violet-700 border-violet-200',
  counsellor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en', { day: '2-digit', month: 'short', year: '2-digit' });
}

/** Discussion thread on one demo — same pattern as the student activity timeline. */
export default function DemoComments({ demoId, readOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [mentions, setMentions] = useState([]);
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editMentions, setEditMentions] = useState([]);

  const load = useCallback(async () => {
    if (!demoId) return;
    setLoading(true);
    try {
      const r = await api.get(`/training/demos/${demoId}/comments`);
      setRows(r.data.comments || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load comments'); }
    finally { setLoading(false); }
  }, [demoId]);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(`/training/demos/${demoId}/comments`, { text: text.trim(), mentioned_user_ids: mentions });
      toast.success('Comment posted');
      setText(''); setMentions([]); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post'); }
    finally { setPosting(false); }
  };

  const saveEdit = async (c) => {
    try {
      await api.put(`/training/demos/${demoId}/comments/${c.id}`, { text: editText.trim(), mentioned_user_ids: editMentions });
      toast.success('Comment updated'); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const remove = async (c) => {
    if (!window.confirm('Delete this comment?')) return;
    try { await api.delete(`/training/demos/${demoId}/comments/${c.id}`); toast.success('Comment deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Card className="p-5 border space-y-3" data-testid="demo-comments">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-[13.5px] font-semibold text-slate-900 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center"><MessageCircle className="w-4 h-4" /></span>
          Discussion <span className="text-slate-400 font-medium">({rows.length})</span>
        </h2>
        <div className="flex items-center gap-1">
          <button onClick={load} title="Refresh" className="p-1.5 text-slate-400 hover:text-slate-600 rounded hover:bg-slate-100" data-testid="demo-comments-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {!readOnly && (
            <Button size="sm" variant={open ? 'outline' : 'default'}
              className={open ? 'h-8 text-[11px]' : 'h-8 text-[11px] bg-violet-600 hover:bg-violet-700'}
              onClick={() => setOpen((o) => !o)} data-testid="demo-comment-add">
              {open ? <><X className="w-3.5 h-3.5 mr-1" />Cancel</> : <><MessageSquarePlus className="w-3.5 h-3.5 mr-1" />Add Comment</>}
            </Button>
          )}
        </div>
      </div>

      {open && !readOnly && (
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-2.5">
          <MentionTextarea
            value={text}
            onChange={setText}
            onMentionsChange={setMentions}
            placeholder="Write a comment… use @ to mention a teammate"
            rows={3}
            data-testid="demo-comment-composer"
          />
          <div className="flex justify-end gap-1 mt-2">
            <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setText(''); setMentions([]); setOpen(false); }}>Cancel</Button>
            <Button size="sm" disabled={!text.trim() || posting} onClick={post} className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700" data-testid="demo-comment-post">
              {posting ? 'Posting…' : 'Post'}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rows.length === 0 && !loading && (
          <p className="text-[12px] text-slate-400 italic text-center py-5" data-testid="demo-comments-empty">
            No comments yet.{readOnly ? '' : ' Be the first to add context to this demo.'}
          </p>
        )}
        {rows.map((c) => {
          const isEditing = editingId === c.id;
          return (
            <div key={c.id} className="flex gap-2.5 group" data-testid={`demo-comment-${c.id}`}>
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-[11px] font-bold">
                {(c.author || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                    <span className="text-[12px] font-semibold text-slate-800 truncate">{c.author || 'Unknown'}</span>
                    <Badge variant="outline" className={`text-[9px] uppercase ${ROLE_META[c.author_role] || ROLE_META.admin}`}>{c.author_role}</Badge>
                    {c.edited && <span className="text-[9.5px] text-slate-400 italic">edited</span>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10.5px] text-slate-400">{timeAgo(c.created_at)}</span>
                    {c.can_edit && !isEditing && !readOnly && (
                      <>
                        <button onClick={() => { setEditingId(c.id); setEditText(c.text || ''); setEditMentions(c.mentioned_user_ids || []); }} className="text-slate-400 hover:text-violet-600 opacity-0 group-hover:opacity-100" title="Edit" data-testid={`demo-comment-edit-${c.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(c)} className="text-slate-400 hover:text-rose-600 opacity-0 group-hover:opacity-100" title="Delete" data-testid={`demo-comment-delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="mt-2">
                    <MentionTextarea value={editText} onChange={setEditText} onMentionsChange={setEditMentions} rows={2} data-testid="demo-comment-edit-box" />
                    <div className="flex gap-1 justify-end mt-1.5">
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setEditingId(null)}>Cancel</Button>
                      <Button size="sm" className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700" onClick={() => saveEdit(c)} data-testid="demo-comment-edit-save">
                        <Save className="w-3.5 h-3.5 mr-1" />Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-[12.5px] text-slate-700 mt-1.5 leading-relaxed whitespace-pre-wrap break-words">
                    <MentionText text={c.text} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
